import { useEffect, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client/react'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import BookingStatusBadge from '../components/customer/BookingStatusBadge'
import BookingStatusTimeline from '../components/customer/BookingStatusTimeline'
import Button from '../components/ui/Button'
import FormError from '../components/ui/FormError'
import {
  CANCEL_BOOKING_MUTATION,
  MY_BOOKINGS_QUERY,
  type CancelBookingData,
  type CancelBookingVariables,
  type CustomerBooking,
  type MyBookingsData,
  type MyBookingsVariables,
} from '../graphql/customer'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized } from '../lib/i18n'

const translations = {
  el: {
    back: 'Πίσω στις κρατήσεις',
    loading: 'Φορτώνουμε τις λεπτομέρειες κράτησης...',
    notFoundTitle: 'Η κράτηση δεν βρέθηκε',
    notFoundBody: 'Δεν βρήκαμε αυτή την κράτηση στον λογαριασμό σου.',
    loadError: 'Δεν μπορέσαμε να φορτώσουμε τα στοιχεία της κράτησης.',
    bookingTitle: 'Λεπτομέρειες κράτησης',
    provider: 'Επαγγελματίας',
    service: 'Υπηρεσία',
    schedule: 'Πρόγραμμα',
    duration: 'Διάρκεια',
    price: 'Τιμή',
    payment: 'Πληρωμή',
    paymentStatus: 'Κατάσταση πληρωμής',
    property: 'Ακίνητο',
    customerNotes: 'Σημειώσεις πελάτη',
    providerNotes: 'Σημειώσεις επαγγελματία',
    none: 'Δεν υπάρχουν.',
    cancelBooking: 'Ακύρωση κράτησης',
    cancelBody: 'Μπορείς να ακυρώσεις όσο η κράτηση είναι ακόμη σε αναμονή ή επιβεβαιωμένη.',
    cancelReason: 'Λόγος ακύρωσης',
    cancelReasonPlaceholder: 'Προαιρετικό μήνυμα προς τον επαγγελματία.',
    confirmCancel: 'Επιβεβαίωση ακύρωσης',
    closeCancel: 'Κλείσιμο',
    cancellationInfo: 'Στοιχεία ακύρωσης',
    cancelledBy: 'Ακυρώθηκε από',
    cancelledAt: 'Ημερομηνία',
    cancellationReasonLabel: 'Λόγος',
    review: 'Άφησε αξιολόγηση',
    reviewHint: 'Η κράτηση ολοκληρώθηκε. Μπορείς τώρα να αφήσεις αξιολόγηση.',
    reviewSubmitted: 'Η αξιολόγησή σου έχει ήδη καταχωριστεί για αυτή την κράτηση.',
    providerProfile: 'Δημόσιο προφίλ',
    minutes: 'λεπτά',
    today: 'Σήμερα',
    tomorrow: 'Αύριο',
    actorCustomer: 'Πελάτη',
    actorProvider: 'Επαγγελματία',
    actorSystem: 'Συστήματος',
  },
  en: {
    back: 'Back to bookings',
    loading: 'Loading booking details...',
    notFoundTitle: 'Booking not found',
    notFoundBody: 'We could not find this booking in your account.',
    loadError: 'We could not load this booking.',
    bookingTitle: 'Booking details',
    provider: 'Provider',
    service: 'Service',
    schedule: 'Schedule',
    duration: 'Duration',
    price: 'Price',
    payment: 'Payment',
    paymentStatus: 'Payment status',
    property: 'Property',
    customerNotes: 'Customer notes',
    providerNotes: 'Provider notes',
    none: 'None added.',
    cancelBooking: 'Cancel booking',
    cancelBody: 'You can cancel while the booking is still pending or confirmed.',
    cancelReason: 'Cancellation reason',
    cancelReasonPlaceholder: 'Optional note for the provider.',
    confirmCancel: 'Confirm cancellation',
    closeCancel: 'Close',
    cancellationInfo: 'Cancellation details',
    cancelledBy: 'Cancelled by',
    cancelledAt: 'Time',
    cancellationReasonLabel: 'Reason',
    review: 'Leave a review',
    reviewHint: 'This booking is complete. You can now leave a review.',
    reviewSubmitted: 'You have already submitted a review for this booking.',
    providerProfile: 'Public profile',
    minutes: 'minutes',
    today: 'Today',
    tomorrow: 'Tomorrow',
    actorCustomer: 'Customer',
    actorProvider: 'Provider',
    actorSystem: 'System',
  },
} as const

function normalizeTime(timeValue: string) {
  return timeValue.slice(0, 5)
}

function formatDate(dateValue: string, locale: 'el' | 'en', copy: { today: string; tomorrow: string }) {
  const today = new Date()
  const date = new Date(`${dateValue}T12:00:00`)
  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)

  if (dateValue === today.toISOString().slice(0, 10)) return copy.today
  if (dateValue === tomorrow.toISOString().slice(0, 10)) return copy.tomorrow

  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function formatDateTime(timestamp: string, locale: 'el' | 'en') {
  return new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp))
}

function formatCurrency(cents: number, locale: 'el' | 'en') {
  return new Intl.NumberFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

function providerName(booking: CustomerBooking) {
  return `${booking.providerProfile.user.firstName} ${booking.providerProfile.user.lastName}`
}

function cancellationActor(cancelledBy: string | null, copy: { actorCustomer: string; actorProvider: string; actorSystem: string }) {
  if (cancelledBy === 'customer') return copy.actorCustomer
  if (cancelledBy === 'provider') return copy.actorProvider
  if (cancelledBy === 'system') return copy.actorSystem
  return cancelledBy
}

const secondaryLinkClass = 'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2'
const primaryLinkClass = 'inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'

export default function BookingDetailPage() {
  const { id } = useParams()
  const client = useApolloClient()
  const { locale, t } = useLang()
  const copy = useScopedTranslations('booking.detail', translations[locale])

  const [booking, setBooking] = useState<CustomerBooking | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [cancelReason, setCancelReason] = useState('')
  const [showCancelForm, setShowCancelForm] = useState(false)

  const [cancelBooking, { loading: cancelling }] = useMutation<CancelBookingData, CancelBookingVariables>(CANCEL_BOOKING_MUTATION)

  useEffect(() => {
    let active = true

    async function loadBooking() {
      if (!id) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setLoading(true)
      setErrors([])
      setNotFound(false)
      setBooking(null)

      try {
        let after: string | null = null

        while (active) {
          const queryResult = await client.query<MyBookingsData, MyBookingsVariables>({
            query: MY_BOOKINGS_QUERY,
            variables: { first: 50, after },
            fetchPolicy: 'network-only',
          }) as { data?: MyBookingsData }

          const queryData = queryResult.data
          if (!queryData) break

          const match = queryData.myBookings.nodes.find((node: CustomerBooking) => node.id === id)
          if (match) {
            setBooking(match)
            setLoading(false)
            return
          }

          if (!queryData.myBookings.pageInfo.hasNextPage || !queryData.myBookings.pageInfo.endCursor) {
            break
          }

          after = queryData.myBookings.pageInfo.endCursor
        }

        if (active) {
          setNotFound(true)
          setLoading(false)
        }
      } catch {
        if (active) {
          setErrors([copy.loadError])
          setLoading(false)
        }
      }
    }

    void loadBooking()

    return () => {
      active = false
    }
  }, [client, copy.loadError, id])

  async function handleCancel() {
    if (!booking) return

    setErrors([])

    try {
      const { data } = await cancelBooking({
        variables: {
          bookingId: booking.id,
          reason: cancelReason.trim() || undefined,
        },
      })

      const mutationErrors = data?.cancelBooking.errors ?? [t('Δεν ήταν δυνατή η ακύρωση.', 'Could not cancel the booking.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        return
      }

      if (data?.cancelBooking.booking) {
        setBooking(data.cancelBooking.booking)
        setShowCancelForm(false)
        setCancelReason('')
      }
    } catch {
      setErrors([t('Δεν ήταν δυνατή η ακύρωση.', 'Could not cancel the booking.')])
    }
  }

  if (loading) {
    return <div className="mx-auto max-w-5xl px-4 py-10 text-sm text-slate-500 md:px-6">{copy.loading}</div>
  }

  if (notFound || !booking) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 md:px-6">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-2xl font-bold text-slate-950">{copy.notFoundTitle}</p>
          <p className="mt-3 text-sm text-slate-600">{copy.notFoundBody}</p>
          <Link to="/bookings" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
            <ArrowLeft className="h-4 w-4" />
            {copy.back}
          </Link>
        </div>
      </div>
    )
  }

  const propertyAddress = booking.property
    ? [booking.property.label, booking.property.addressLine1, booking.property.city, booking.property.postalCode].filter(Boolean).join(', ')
    : null
  const canCancel = ['pending', 'confirmed'].includes(booking.status)
  const canReview = booking.status === 'completed' && !booking.review

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-10">
      <Link to="/bookings" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
        <ArrowLeft className="h-4 w-4" />
        {copy.back}
      </Link>

      <section className="mt-6 rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#eff6ff_100%)] p-6 shadow-sm md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <BookingStatusBadge status={booking.status} />
            <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-950">{copy.bookingTitle}</h1>
            <p className="mt-3 text-lg leading-8 text-slate-600">
              {localized(booking.serviceType, locale)} · {formatDate(booking.scheduledDate, locale, copy)} · {normalizeTime(booking.startTime)} - {normalizeTime(booking.endTime)}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to={`/providers/${booking.providerProfile.id}`} className={secondaryLinkClass}>
              {copy.providerProfile}
              <ExternalLink className="h-4 w-4" />
            </Link>
            {canCancel && !showCancelForm && (
              <Button type="button" variant="ghost" onClick={() => setShowCancelForm(true)}>
                {copy.cancelBooking}
              </Button>
            )}
            {canReview && (
              <Link to={`/bookings/${booking.id}/review`} className={primaryLinkClass}>
                {copy.review}
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="mt-6">
        <FormError errors={errors} />
      </div>

      <div className="mt-8">
        <BookingStatusTimeline
          status={booking.status}
          confirmedAt={booking.confirmedAt}
          startedAt={booking.startedAt}
          completedAt={booking.completedAt}
        />
      </div>

      {booking.status === 'completed' && (
        <div className="mt-6 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-800">
          {booking.review ? copy.reviewSubmitted : copy.reviewHint}
        </div>
      )}

      {showCancelForm && canCancel && (
        <section className="mt-8 rounded-[1.75rem] border border-rose-100 bg-rose-50 p-6">
          <h2 className="text-2xl font-bold text-slate-950">{copy.cancelBooking}</h2>
          <p className="mt-3 text-sm leading-7 text-slate-600">{copy.cancelBody}</p>
          <label className="mt-5 flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-700">{copy.cancelReason}</span>
            <textarea
              rows={4}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder={copy.cancelReasonPlaceholder}
              className="w-full rounded-xl border border-rose-100 bg-white px-4 py-3 text-sm text-slate-900 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100"
            />
          </label>
          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowCancelForm(false)}>
              {copy.closeCancel}
            </Button>
            <Button type="button" variant="danger" loading={cancelling} onClick={handleCancel}>
              {copy.confirmCancel}
            </Button>
          </div>
        </section>
      )}

      <section className="mt-8 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-8">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-950">{copy.bookingTitle}</h2>
            <dl className="mt-6 grid gap-5 md:grid-cols-2">
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.provider}</dt>
                <dd className="mt-1 text-sm text-slate-600">{providerName(booking)}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.service}</dt>
                <dd className="mt-1 text-sm text-slate-600">{localized(booking.serviceType, locale)}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.schedule}</dt>
                <dd className="mt-1 text-sm text-slate-600">{formatDate(booking.scheduledDate, locale, copy)} · {normalizeTime(booking.startTime)} - {normalizeTime(booking.endTime)}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.duration}</dt>
                <dd className="mt-1 text-sm text-slate-600">{booking.durationMinutes} {copy.minutes}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.price}</dt>
                <dd className="mt-1 text-sm text-slate-600">{formatCurrency(booking.priceCents, locale)}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.payment}</dt>
                <dd className="mt-1 text-sm text-slate-600">{booking.paymentMethod ?? copy.none}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold text-slate-950">{copy.paymentStatus}</dt>
                <dd className="mt-1 text-sm text-slate-600">{booking.paymentStatus}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-950">{copy.property}</h2>
            {propertyAddress ? (
              <div className="mt-5 space-y-3 text-sm text-slate-600">
                <p>{propertyAddress}</p>
                <p>
                  {booking.property?.sizeSqm ? `${booking.property.sizeSqm} sqm` : null}
                  {booking.property?.floor !== null ? ` · ${t('Όροφος', 'Floor')} ${booking.property?.floor}` : ''}
                  {booking.property?.bedrooms !== null ? ` · ${booking.property?.bedrooms} ${t('υ/δ', 'beds')}` : ''}
                  {booking.property?.bathrooms !== null ? ` · ${booking.property?.bathrooms} ${t('μπάνια', 'baths')}` : ''}
                </p>
              </div>
            ) : (
              <p className="mt-5 text-sm text-slate-500">{copy.none}</p>
            )}
          </div>
        </div>

        <div className="space-y-8">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-950">{copy.customerNotes}</h2>
            <p className="mt-4 text-sm leading-7 text-slate-600">{booking.customerNotes || copy.none}</p>

            <h3 className="mt-8 text-lg font-bold text-slate-950">{copy.providerNotes}</h3>
            <p className="mt-3 text-sm leading-7 text-slate-600">{booking.providerNotes || copy.none}</p>
          </div>

          {booking.status === 'cancelled' && (
            <div className="rounded-[1.75rem] border border-rose-100 bg-rose-50 p-6">
              <h2 className="text-2xl font-bold text-slate-950">{copy.cancellationInfo}</h2>
              <dl className="mt-5 space-y-4 text-sm text-slate-700">
                <div>
                  <dt className="font-semibold text-slate-950">{copy.cancelledBy}</dt>
                  <dd className="mt-1">{cancellationActor(booking.cancelledBy, copy)}</dd>
                </div>
                {booking.cancelledAt && (
                  <div>
                    <dt className="font-semibold text-slate-950">{copy.cancelledAt}</dt>
                    <dd className="mt-1">{formatDateTime(booking.cancelledAt, locale)}</dd>
                  </div>
                )}
                <div>
                  <dt className="font-semibold text-slate-950">{copy.cancellationReasonLabel}</dt>
                  <dd className="mt-1">{booking.cancellationReason || copy.none}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
